import React from 'react';
import Icon from './Icon';

/**
 * Cockpit IconButton — square quiet control for chrome actions
 * (bell, refresh, close, overflow menus).
 */
export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string;
  variant?: 'quiet' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  spin?: boolean;
  dot?: boolean;
  active?: boolean;
}

const dims = { sm: 'var(--control-sm)', md: 'var(--control-md)', lg: 'var(--control-lg)' };

export default function IconButton({
  icon,
  variant = 'quiet',
  size = 'md',
  label,
  spin = false,
  dot = false,
  active = false,
  disabled = false,
  onClick,
  style,
  ...rest
}: IconButtonProps) {
  const variants: Record<string, React.CSSProperties> = {
    quiet: {
      background: active ? 'var(--surface-active)' : 'transparent',
      border: '1px solid transparent',
      color: active ? 'var(--text-body)' : 'var(--text-muted)',
    },
    outline: {
      background: 'var(--surface-raised)',
      border: '1px solid var(--border-hairline)',
      color: 'var(--text-muted)',
    },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`ck-iconbtn pressable ${spin ? 'hover-spin' : 'hover-wash'}`}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: dims[size],
        height: dims[size],
        borderRadius: 'var(--radius-sm)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        ...variants[variant],
        ...style,
      }}
      {...rest}
    >
      <Icon name={icon} size={size === 'sm' ? 14 : 16} />
      {dot ? (
        <span
          style={{
            position: 'absolute',
            top: '6px',
            right: '6px',
            width: '7px',
            height: '7px',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--accent)',
            boxShadow: '0 0 0 2px var(--surface-canvas)',
          }}
        />
      ) : null}
    </button>
  );
}
